import formatCurrency from "constants/formatCurrency";

/**
 * Compute stats for one or many platform objects
 * @param {Array|Object} data - single platform object or array of them
 * @param {boolean} withCharts - whether to return chart-ready structures
 */
export function computeReportStats(data, withCharts = false) {
    const platformsList = Array.isArray(data) ? data : [data];

    let totals = {
        corepark: 0,
        spothero: 0,
        resolve: 0,
        surepark: 0,
        amano: 0,
        monthly: 0,
        lease: 0,
        fee: 0,
        cost: 0,
        prospr: 0,
        events_income: 0,
        events_expense: 0,
    };

    const locationIds = [];
    let start_date = "";
    let end_date = "";
    let account_manager = "";
    const generalEvents = [];
    const locationEvents = {};

    platformsList.forEach(platforms => {
        if (platforms.id) locationIds.push(platforms.id);
        if (platforms.start_date) start_date = platforms.start_date;
        if (platforms.end_date) end_date = platforms.end_date;
        if (platforms.account_manager) account_manager = platforms.account_manager;

        const corepark = platforms.corepark || {};
        const spothero = platforms.spothero || {};
        const resolve = platforms.resolve || {};
        const surepark = platforms.surepark || {};
        const amano = platforms.amano || {};
        const monthly = platforms.monthly_parking_orders || {};
        const lease = platforms.lease_payments || {};
        const fee = platforms.fees || {};
        const cost = platforms.costs || {};
        const prospr = platforms.prospr || {};
        const events = platforms.events || {};

        totals.corepark += (corepark.cash || 0) + (corepark.card || 0); // + (corepark.tax || 0) + (corepark.fee || 0) + (corepark.tip || 0)
        totals.spothero += spothero.amount || 0;
        totals.resolve += resolve.net_revenue || 0;
        totals.surepark += surepark.amount || 0;
        totals.amano += amano.fee - amano.vat || 0;
        totals.monthly += monthly.amount || 0;
        totals.lease += lease.amount || 0;
        totals.fee += fee.amount || 0;
        totals.cost += cost.amount || 0;
        totals.prospr += prospr.cost || 0;

        // --- events totals
        totals.events_income += events.income || 0;
        totals.events_expense += (events.labor || 0) + (events.non_labor || 0);

        // classify events
        if (platforms.id === null) {
            generalEvents.push(events);
        } else {
            locationEvents[platforms.id] = events;
        }
    });

    const allFeeItems = platformsList.flatMap(p => p.fees?.payments || []);
    const labor = {
        billable_flat_labor: platformsList[0]?.fees?.billable_flat_labor ?? 0,
        billable_payload_percentage: platformsList[0]?.fees?.billable_payload_percentage ?? 0,
        billable_manager_rate: platformsList[0]?.fees?.billable_manager_rate ?? 0,
        manager_labor_hours: platformsList[0]?.fees?.manager_labor_hours ?? 0,
        billable_attendant_rate: platformsList[0]?.fees?.billable_attendant_rate ?? 0,
        attendant_labor_hours: platformsList[0]?.fees?.attendant_labor_hours ?? 0,
        total_labor_hours: platformsList[0]?.prospr?.hours,
        total_labor_cost: platformsList[0]?.prospr?.cost,
    }
    if (labor.manager_labor_hours > 0 || labor.attendant_labor_hours > 0) {
        allFeeItems.push({
            amount: labor.total_labor_hours * labor.billable_manager_rate,
            type: "Billable Labor",
        });
        totals.fee += labor.total_labor_hours * labor.billable_manager_rate;
    }

    const revenueSources = [
        { label: "CorePark", amount: totals.corepark, details: platformsList[0]?.corepark },
        { label: "SpotHero", amount: totals.spothero },
        { label: "Resolve", amount: totals.resolve },
        { label: "SurePark", amount: totals.surepark },
        { label: "Amano", amount: totals.amano },
        { label: "Monthly Parking", amount: totals.monthly, details: platformsList[0]?.monthly_parking_orders },
        { label: "Lease Payments", amount: totals.lease },
        { label: "Fees", amount: totals.fee, items: allFeeItems, labor },
        { label: "Events Income", amount: totals.events_income },
    ].filter(r => r.amount);

    const allCostItems = platformsList.flatMap(p => p.costs?.payments || []);

    const laborExpenseAmount = platformsList.reduce((sum, p) => sum + (p.prospr?.cost || 0), 0);

    const expenseSources = [
        { label: "Costs", amount: totals.cost, items: allCostItems },
        { label: "Labor", amount: laborExpenseAmount },
        { label: "Events Expense", amount: totals.events_expense }
    ].filter(e => e.amount);

    const base = {
        revenues: revenueSources,
        expenses: expenseSources,
        locationIds,
        start_date,
        end_date,
        account_manager,
        generalEvents,
        locationEvents
    };

    if (!withCharts) return base;

    return {
        ...base,
        revenueChart: {
            title: "Total Revenue",
            total: formatCurrency(revenueSources.reduce((s, r) => s + r.amount, 0)),
            labels: revenueSources.map(r => r.label),
            series: revenueSources.map(r => r.amount),
        },
        expenseChart: {
            title: "Total Expense",
            total: formatCurrency(expenseSources.reduce((s, e) => s + e.amount, 0)),
            labels: expenseSources.map(e => e.label),
            series: expenseSources.map(e => e.amount),
        },
    };
}